import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Menu, X, ChevronDown, Sun, Moon, LogIn, LayoutDashboard } from 'lucide-react';
import { useTheme } from '@/components/ThemeProvider';
import { useBranding, useAuth } from '@/App';

const navLinks = [
  { label: 'Home', href: '/' },
  { label: 'Features', href: '/#features' },
  { label: 'How it Works', href: '/#how-it-works' },
  { label: 'Pricing', href: '/#pricing' },
  { label: 'Blogs', href: '/blogs' },
];

const docSubLinks = [
  { label: 'Payment Verification', href: '/docs' },
  { label: 'Payment Notifications', href: '/docs/notifications' },
  { label: 'Integrations', href: '/docs/integrations' },
];

export const SiteHeader = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [docsOpen, setDocsOpen] = useState(false);
  const [mobileDocsOpen, setMobileDocsOpen] = useState(false);
  const docsRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const { brand_name, logo_data, logo_dark_data, logo_light_data } = useBranding();
  const activeLogo = (theme === 'dark' ? logo_dark_data : logo_light_data) || logo_data;
  const { user } = useAuth();
  const isLoggedInMerchant = user && user.role === 'merchant';
  const dashboardPath = '/dashboard';

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (docsRef.current && !docsRef.current.contains(e.target)) setDocsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleNavClick = (href) => {
    setMobileOpen(false);
    setDocsOpen(false);
    if (href === '/') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else if (href.startsWith('/#') && location.pathname === '/') {
      const id = href.replace('/#', '');
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const isDocsActive = location.pathname.startsWith('/docs');

  return (
    <header className="border-b border-border bg-background/80 backdrop-blur-xl sticky top-0 z-50" data-testid="site-header">
      <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2.5 group">
          {activeLogo ? (
            <img src={activeLogo} alt={brand_name} className="h-8 max-w-[120px] object-contain" />
          ) : (
            <>
              <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center shadow-[0_0_16px_rgba(99,102,241,0.4)] group-hover:shadow-[0_0_24px_rgba(99,102,241,0.6)] transition-shadow">
                <span className="text-white font-bold text-sm">C</span>
              </div>
              <span className="font-semibold text-lg tracking-tight text-foreground" style={{ fontFamily: "'Outfit', sans-serif" }}>{brand_name || 'CoinPayUp'}</span>
            </>
          )}
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-8">
          {navLinks.map(link => (
            <Link key={link.href} to={link.href} onClick={() => handleNavClick(link.href)}
              className="text-sm text-muted-foreground hover:text-foreground transition-colors font-medium">
              {link.label}
            </Link>
          ))}
          {/* Documentation Dropdown */}
          <div ref={docsRef} className="relative">
            <button
              onClick={() => setDocsOpen(!docsOpen)}
              data-testid="docs-dropdown-trigger"
              className={`flex items-center gap-1 text-sm font-medium transition-colors ${isDocsActive ? 'text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
            >
              Documentation
              <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-200 ${docsOpen ? 'rotate-180' : ''}`} />
            </button>
            {docsOpen && (
              <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-56 rounded-xl border border-border bg-background/95 backdrop-blur-xl shadow-lg py-1.5 z-50" data-testid="docs-dropdown-menu">
                {docSubLinks.map(sub => (
                  <Link
                    key={sub.href}
                    to={sub.href}
                    onClick={() => handleNavClick(sub.href)}
                    data-testid={`docs-link-${sub.href.replace(/\//g, '-')}`}
                    className={`block px-4 py-2.5 text-sm transition-colors ${location.pathname === sub.href ? 'text-primary bg-primary/5 font-medium' : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'}`}
                  >
                    {sub.label}
                  </Link>
                ))}
              </div>
            )}
          </div>
        </nav>

        {/* CTA */}
        <div className="hidden md:flex items-center gap-3">
          <button
            onClick={toggleTheme}
            className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-all"
            aria-label="Toggle theme"
            data-testid="theme-toggle-btn"
          >
            {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </button>
          <Link to={isLoggedInMerchant ? dashboardPath : '/login'}>
            <Button size="sm" className="bg-primary text-white shadow-[0_0_16px_rgba(99,102,241,0.4)] hover:shadow-[0_0_24px_rgba(99,102,241,0.6)] transition-all" data-testid="header-login-btn">
              {isLoggedInMerchant
                ? <><LayoutDashboard className="h-3.5 w-3.5 mr-1.5" /> Go to Dashboard</>
                : <><LogIn className="h-3.5 w-3.5 mr-1.5" /> Account Login</>}
            </Button>
          </Link>
        </div>

        {/* Mobile Toggle */}
        <button className="md:hidden p-2 text-muted-foreground" onClick={() => setMobileOpen(!mobileOpen)}>
          {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="md:hidden border-t border-border bg-background/95 backdrop-blur-xl px-6 py-4 space-y-1">
          {navLinks.map(link => (
            <Link key={link.href} to={link.href} onClick={() => handleNavClick(link.href)}
              className="block text-sm text-muted-foreground hover:text-foreground py-2 font-medium">{link.label}</Link>
          ))}
          {/* Mobile Documentation Accordion */}
          <button
            onClick={() => setMobileDocsOpen(!mobileDocsOpen)}
            data-testid="mobile-docs-toggle"
            className="w-full flex items-center justify-between text-sm text-muted-foreground hover:text-foreground py-2 font-medium"
          >
            Documentation
            <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-200 ${mobileDocsOpen ? 'rotate-180' : ''}`} />
          </button>
          {mobileDocsOpen && (
            <div className="pl-4 space-y-1 border-l-2 border-border ml-1">
              {docSubLinks.map(sub => (
                <Link key={sub.href} to={sub.href} onClick={() => handleNavClick(sub.href)}
                  data-testid={`mobile-docs-link-${sub.href.replace(/\//g, '-')}`}
                  className={`block text-sm py-2 ${location.pathname === sub.href ? 'text-primary font-medium' : 'text-muted-foreground hover:text-foreground'}`}>
                  {sub.label}
                </Link>
              ))}
            </div>
          )}
          <div className="pt-3 flex flex-col gap-2">
            <button onClick={toggleTheme} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground py-2 font-medium">
              {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
              {theme === 'dark' ? 'Light Mode' : 'Dark Mode'}
            </button>
            <Link to={isLoggedInMerchant ? dashboardPath : '/login'}>
              <Button size="sm" className="w-full bg-primary text-white">
                {isLoggedInMerchant ? 'Go to Dashboard' : 'Account Login'}
              </Button>
            </Link>
          </div>
        </div>
      )}
    </header>
  );
};

export const SiteFooter = () => {
  const { brand_name, logo_data, logo_dark_data, logo_light_data } = useBranding();
  const { theme } = useTheme();
  const activeLogo = (theme === 'dark' ? logo_dark_data : logo_light_data) || logo_data;
  const year = new Date().getFullYear();

  return (
    <footer className="bg-[hsl(var(--card))] border-t border-border" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-6 pt-14 pb-8">
        {/* Main grid */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-10 mb-12">

          {/* Brand column */}
          <div className="col-span-2">
            <Link to="/" className="flex items-center gap-2.5 mb-4">
              {activeLogo ? (
                <img src={activeLogo} alt={brand_name} className="h-8 max-w-[130px] object-contain" />
              ) : (
                <>
                  <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
                    <span className="text-white font-bold text-sm">C</span>
                  </div>
                  <span className="font-bold text-base tracking-tight">{brand_name || 'CoinPayUp'}</span>
                </>
              )}
            </Link>
            <p className="text-sm text-muted-foreground leading-relaxed max-w-xs mb-5">
              Non-custodial crypto payment verification for modern businesses. Accept USDT, USDC, BNB and more with zero custody risk.
            </p>
            {/* Trust badges */}
            <div className="flex flex-wrap gap-2">
              <span className="inline-flex items-center gap-1.5 text-[11px] font-medium text-muted-foreground bg-muted/40 border border-border rounded-full px-2.5 py-1">
                <svg className="w-3 h-3 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" /></svg>
                Non-Custodial
              </span>
              <span className="inline-flex items-center gap-1.5 text-[11px] font-medium text-muted-foreground bg-muted/40 border border-border rounded-full px-2.5 py-1">
                <svg className="w-3 h-3 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" /></svg>
                SSL Secured
              </span>
              <span className="inline-flex items-center gap-1.5 text-[11px] font-medium text-muted-foreground bg-muted/40 border border-border rounded-full px-2.5 py-1">
                <svg className="w-3 h-3 text-purple-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M13.19 8.688a4.5 4.5 0 011.242 7.244l-4.5 4.5a4.5 4.5 0 01-6.364-6.364l1.757-1.757m13.35-.622l1.757-1.757a4.5 4.5 0 00-6.364-6.364l-4.5 4.5a4.5 4.5 0 001.242 7.244" /></svg>
                On-Chain Verified
              </span>
            </div>
          </div>

          {/* Product */}
          <div>
            <h4 className="text-xs font-bold uppercase tracking-wider text-foreground mb-4">Product</h4>
            <ul className="space-y-3">
              <li><Link to="/#features" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Features</Link></li>
              <li><Link to="/#pricing" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Pricing</Link></li>
              <li><Link to="/docs" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Documentation</Link></li>
              <li><Link to="/docs/integrations" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Integrations</Link></li>
              <li><Link to="/#downloads" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Downloads</Link></li>
            </ul>
          </div>

          {/* Resources */}
          <div>
            <h4 className="text-xs font-bold uppercase tracking-wider text-foreground mb-4">Resources</h4>
            <ul className="space-y-3">
              <li><Link to="/blogs" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Blog</Link></li>
              <li><Link to="/docs/notifications" className="text-sm text-muted-foreground hover:text-foreground transition-colors">API Docs</Link></li>
              <li><Link to="/support" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Support Center</Link></li>
              <li><Link to="/knowledge-base" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Knowledge Base</Link></li>
              <li><Link to="/login" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Merchant Login</Link></li>
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h4 className="text-xs font-bold uppercase tracking-wider text-foreground mb-4">Legal</h4>
            <ul className="space-y-3">
              <li><Link to="/privacy" className="text-sm text-muted-foreground hover:text-foreground transition-colors" data-testid="footer-privacy-link">Privacy Policy</Link></li>
              <li><Link to="/terms" className="text-sm text-muted-foreground hover:text-foreground transition-colors" data-testid="footer-terms-link">Terms &amp; Conditions</Link></li>
            </ul>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="border-t border-border/60 pt-6 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-xs text-muted-foreground">
            &copy; {year} {brand_name || 'CoinPayUp'}. All rights reserved.
          </p>
          <div className="flex items-center gap-5">
            <Link to="/privacy" className="text-xs text-muted-foreground hover:text-foreground transition-colors">Privacy Policy</Link>
            <span className="text-muted-foreground/30">·</span>
            <Link to="/terms" className="text-xs text-muted-foreground hover:text-foreground transition-colors">Terms of Service</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};
